import {
    InboxOutlined,
    MinusCircleOutlined,
    UploadOutlined,
    PlusOutlined,
    ArrowLeftOutlined,
    CheckCircleOutlined,
    InfoCircleOutlined,
    ExclamationCircleOutlined,
    LoadingOutlined,
    DeleteOutlined,
    PaperClipOutlined,
} from "@ant-design/icons";
// import { Turnstile } from "@marsidev/react-turnstile";
import type { TurnstileInstance } from '@marsidev/react-turnstile'
import {
    Card,
    Typography,
    Form,
    message,
    Input,
    type UploadFile,
    type UploadProps,
    Button,
    Progress,
    Layout,
    Upload,
    Divider,
    Row,
    Col,
    Tag,
    Select,
    Checkbox,
    Grid,
    Spin,
    Tooltip,
    Radio,
} from "antd";
import type { RcFile } from "antd/es/upload";
import axios, { type AxiosProgressEvent } from "axios";
import { useRef, useState, type ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { InfoModal } from "../ui/InfoModal";
import vehicleClassesImg from "../../assets/vehicle_classes.png";
import { useParkingLocationConfigs } from "@/hooks/useParkingLocationConfigs";
import { useDropdownOptions } from "@/hooks/useDropdownOptions";
import { useFormFieldConfigs } from "@/hooks/useFormFieldConfigs";

const { Dragger } = Upload;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;
const { Content } = Layout;

// --- Types ---
type CustomUploadFile = UploadFile & {
    response?: {
        ossKey: string;
    };
};

type CompanyFormValues = {
    companyRegistrationNumber: string;
    tinNumber: string;
    companyName: string;
    contactPerson: string;
    contactNumber: string;
    contactEmail: string;
    companyAddressLine1: string;
    companyAddressLine2?: string;
    plateNumbers?: Array<{
        plateNumber: string;
        vehicleType?: string;
    }>;
    IDType: string;
    IDNumber: string;
    vehiclePassType: number;
    consentPDPA: boolean;
    consentTOC: boolean;
}

interface CreateCompanyRequest {
    companyRegistrationNumber: string;
    tinNumber: string;
    companyName: string;
    contactPerson: string;
    contactNumber: string;
    contactEmail: string;
    companyAddressLine1: string;
    companyAddressLine2?: string;
    companyPlates: Array<{
        plateNumber: string;
        vehicleType: string;
    }>;
    IDType: string;
    IDNumber: string; 
    vehiclePassType: number;
    cardType: string;
}

interface CreateCompanyResponse {
    success: boolean;
    message: string;
    data: {
        employerID: string;
        registerID: string;
    };
}

interface PlateFileEntry {
    plateNumber: string;
    vehicleType: string;
    vehiclePath: string;
}

interface SupportingFileEntry {
    ssmPath: string;
    electricBillPath: string;
    tenantAgreementPath?: string;
}

interface FinalizeCompanyRequest {
    employerID: string;
    companyRegistrationNumber: string;
    tinNumber: string;
    companyName: string;
    contactPerson: string;
    contactNumber: string;
    contactEmail: string;
    companyAddressLine1: string;
    companyAddressLine2: string;
    isTenant: boolean;
    companyPlates: PlateFileEntry[];
    companySupportingFiles: SupportingFileEntry;
    IDType: string;
    IDNumber: string;
    vehiclePassType: number;
    cardType: string;
}

interface FinalizeCompanyResponse {
    id: string;
}

interface UploadResult {
    type: 'electricity' | 'ssm' | 'plate' | 'companyVehicle' | 'tenantAgreement' | 'tenancy';
    uid: string;
    originalName: string;
    ossKey?: string;
    error?: Error;
    plateIndex?: number;
    plateNumber?: string;
    vehicleType?: string;
    subType?: 'spa' | 'electricBill' | 'vehicle';
}

interface UploadTokenResponse {
    success: boolean;
    message: string;
    data: {
        uploadToken: string;
        expiresIn: number;
    };
}

interface DropdownOption {
    id: number;
    option_category: string;
    option_key: string;
    option_value: string;
    description: string;
    display_order: number;
    is_active: boolean;
    metadata: string;
}

interface DropdownOptionsResponse {
    data: DropdownOption[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
}

// Update PlateFileList type to track three separate files per vehicle
type PlateFileList = Record<number, {
    vehicleFile?: CustomUploadFile[];
}>;

const vehicleOptions = [
    { value: '1', label: 'Class 1' },
    // { value: '2', label: 'Class 2' },
    // { value: '3', label: 'Class 3' },
    // { value: '4', label: 'Class 4' },
    // { value: '5', label: 'Class 5' }
];

// --- Config ---
const apiBase = process.env.BUN_PUBLIC_OPP_API_BASE;
const siteKey = process.env.BUN_PUBLIC_TURNSTILE_SITE_KEY || '';

export function CompanyForm() {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [messageApi, contextHolder] = message.useMessage();
    const screens = useBreakpoint();
    const [searchParams] = useSearchParams();
    const [isTenant, setIsTenant] = useState<boolean | null>(null);

    // State for files
    const [plateFilesList, setPlateFilesList] = useState<PlateFileList>({});
    const [ssmFileList, setSsmFileList] = useState<CustomUploadFile[]>([]);
    const [electricityBillFileList, setElectricityBillFileList] = useState<CustomUploadFile[]>([]);
    const [tenantAgreementFileList, setTenantAgreementFileList] = useState<CustomUploadFile[]>([]);

    // Upload state
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
    const [uploadResults, setUploadResults] = useState<UploadResult[]>([]);
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

    const [uploadToken, setUploadToken] = useState<string | null>(null);

    // Use for turnstile reset
    const turnstileRef = useRef<TurnstileInstance | null>(null)

    // Info Modal state
    const [modalConfig, setModalConfig] = useState<{
        open: boolean;
        title: string;
        content?: ReactNode;
        image?: string;
    }>({ open: false, title: '' });

    // Responsive configuration
    const getResponsiveConfig = () => {
        return {
            // Layout configuration
            layout: {
                // Padding
                containerPadding: screens.xs ? '16px' : screens.sm ? '20px' : '24px',
                cardPadding: screens.xs ? '16px' : '24px',

                // Spacing
                gutter: screens.xs ? 16 : screens.sm ? 20 : 24,

                // Max width
                maxWidth: screens.xs ? '100%' : screens.sm ? '70%' : screens.md ? '70%' : '1000px',

                // Column configuration for form
                formColumns: {
                    xs: 24,
                    sm: 24,
                    md: 12,
                    lg: 12,
                    xl: 12
                }
            },

            // Typography
            typography: {
                titleSize: screens.xs ? '1.5rem' : screens.sm ? '1.75rem' : '2rem',
                textSize: screens.xs ? '14px' : '16px'
            }
        };
    };

    const responsiveConfig = getResponsiveConfig();

    // Fetach field enable/disable configs
    const { isFieldEnabled, loading: fieldConfigLoading } = useFormFieldConfigs(searchParams.get('id'), 'company');

    // Fetch the options
    const locationId = searchParams.get('id');
    const { config, loading: configLoading } = useParkingLocationConfigs(locationId, 'company');
    const { options: idType, loading: idTypeLoading } = useDropdownOptions(locationId, 'IDType');
    const { options: vehiclePassType, loading: vehiclePassTypesLoading } = useDropdownOptions(locationId, 'VehiclePassType');

    // --- File Handlers ---
    const handleVehicleFileChange = (index: number) => ({ fileList }: { fileList: UploadFile[] }) => {
        setPlateFilesList(prev => ({
            ...prev,
            [index]: {
                ...prev[index],
                vehicleFile: fileList as CustomUploadFile[]
            }
        }));
        form.validateFields([['plateNumbers', index, 'vehicleDoc']]);
    };

    const handleSsmFileChange: UploadProps['onChange'] = ({ fileList }) => {
        setSsmFileList(fileList as CustomUploadFile[]);
        form.validateFields(['ssmFile']);
    };

    const handleElectricityBillFileChange: UploadProps['onChange'] = ({ fileList }) => {
        setElectricityBillFileList(fileList as CustomUploadFile[]);
        form.validateFields(['electricityFiles']);
    }

    const handleTenantAgreementFileChange: UploadProps['onChange'] = ({ fileList }) => {
        setTenantAgreementFileList(fileList as CustomUploadFile[]);
        form.validateFields(['tenantAgreementFile']);
    }

    const dummyRequest: UploadProps['customRequest'] = ({ onSuccess }) => {
        setTimeout(() => { if (onSuccess) onSuccess("ok"); }, 0);
    };

    // --- Upload Functions ---
    const putFileToOSS = async (signedUrl: string, file: RcFile, onProgress?: (event: AxiosProgressEvent) => void) => {
        await axios.put(signedUrl, file, {
            headers: {
                'Content-Type': file.type || 'application/octet-stream',
            },
            onUploadProgress: onProgress,
        });
    };

    const updateProgress = (event: AxiosProgressEvent, uid: string) => {
        if (event.total) {
            const percent = Math.round((event.loaded / event.total) * 100);
            setUploadProgress(prev => ({ ...prev, [uid]: percent }));
        }
    };

    // Validate file before upload
    const validateFile = (file: RcFile): string | null => {
        try {
            // Check if config is loaded
            if (!config) {
                return t("Configuration not loaded. Please wait...");
            }

            // Check file size
            const maxSize = (config.maxFileSizeMB || 20) * 1024 * 1024;
            if (file.size > maxSize) {
                return t("File size exceeds {{maxSize}}MB limit", {
                    maxSize: config.maxFileSizeMB || 20,
                });
            }

            // Check if file has a type
            if (!file.type) {
                const fileName = file.name.toLowerCase();
                const extension = fileName.split('.').pop();

                const allowedExtensions = ['.jpg', '.jpeg', '.png', '.pdf'];
                if (extension && !allowedExtensions.some(ext => fileName.endsWith(ext))) {
                    return t("File type not recognized. Allowed: PDF, JPG, PNG");
                }
                return null;
            }

            // Check file type against allowed types
            const allowedTypes = config.allowedTypes || ['image/jpeg', 'image/png', 'application/pdf'];
            const isAllowed = allowedTypes.some(allowedType =>
                file.type?.startsWith(allowedType)
            );

            if (!isAllowed) {
                const fileName = file.name.toLowerCase();
                const isExtensionAllowed =
                    fileName.endsWith('.pdf') ||
                    fileName.endsWith('.jpg') ||
                    fileName.endsWith('.jpeg') ||
                    fileName.endsWith('.png')

                if (!isExtensionAllowed) {
                    return t("File type not allowed. Allowed types: {{allowedTypes}}", {
                        allowedTypes: allowedTypes.join(', '),
                    });
                }
            }

            return null;
        } catch (error) {
            console.error('Error validating file:', error);
            return 'Error validating file. Please try again.';
        }
    };

    // Process all uploads with concurrency control
    const processUploadQueue = async (companyId: string, values: CompanyFormValues): Promise<UploadResult[]> => {
        const uploadTasks: Promise<UploadResult>[] = [];
        const plateNumbers = values.plateNumbers || [];

        // Prepare vehicle document upload tasks (SPA, Electric Bill, Vehicle Document for each vehicle)
        plateNumbers.forEach((plateNumber, index) => {
            if (!plateNumber.plateNumber || plateNumber.plateNumber.trim() === '') return;

            // Upload Vehicle Document file for this vehicle
            const vehicleFiles = plateFilesList[index]?.vehicleFile || [];
            vehicleFiles.forEach(file => {
                if (file.originFileObj && file.status !== 'done') {
                    const task = async (): Promise<UploadResult> => {
                        const fileUid = file.uid;
                        const originalName = file.name;

                        try {
                            const validationError = validateFile(file.originFileObj as RcFile);
                            if (validationError) {
                                throw new Error(validationError);
                            }

                            const { data } = await axios.get(`${apiBase}/presigned`, {
                                params: {
                                    registerId: companyId,
                                    fileType: 'plate',
                                    fileName: `vehicle_${file.name}`,
                                    plateNumber: encodeURIComponent(plateNumber.plateNumber),
                                    contentType: file.type || 'application/octet-stream'
                                },
                                // headers: {
                                //     Authorization: `Bearer ${uploadToken}`
                                // }
                            });

                            await putFileToOSS(data.data.url, file.originFileObj as RcFile,
                                (event) => updateProgress(event, fileUid));

                            setPlateFilesList(prev => ({
                                ...prev,
                                [index]: {
                                    ...prev[index],
                                    vehicleFile: (prev[index]?.vehicleFile || []).map(f => f.uid === fileUid
                                        ? { ...f, status: 'done', response: { ossKey: data.data.key } }
                                        : f
                                    )
                                }
                            }));

                            return {
                                type: 'plate',
                                uid: fileUid,
                                originalName,
                                ossKey: data.data.key,
                                plateIndex: index,
                                plateNumber: plateNumber.plateNumber,
                                vehicleType: plateNumber.vehicleType,
                                subType: 'vehicle'
                            };

                        } catch (error) {
                            console.error(`Upload failed for Vehicle Document ${originalName}:`, error);

                            setPlateFilesList(prev => ({
                                ...prev,
                                [index]: {
                                    ...prev[index],
                                    vehicleFile: (prev[index]?.vehicleFile || []).map(f => f.uid === fileUid
                                        ? { ...f, status: 'error', error: error as Error }
                                        : f
                                    )
                                }
                            }));

                            return {
                                type: 'plate',
                                uid: fileUid,
                                originalName,
                                error: error as Error,
                                plateIndex: index,
                                plateNumber: plateNumber.plateNumber,
                                vehicleType: plateNumber.vehicleType,
                                subType: 'vehicle'
                            };
                        }
                    };

                    uploadTasks.push(task());
                }
            });
        });

        // Prepare SSM file upload task
        ssmFileList.forEach(file => {
            if (file.originFileObj && file.status !== 'done') {
                const task = async (): Promise<UploadResult> => {
                    const fileUid = file.uid;
                    const originalName = file.name;

                    try {
                        const validationError = validateFile(file.originFileObj as RcFile);
                        if (validationError) {
                            throw new Error(validationError);
                        }

                        const { data } = await axios.get(`${apiBase}/presigned`, {
                            params: {
                                registerId: companyId,
                                fileType: 'general',
                                fileName: file.name,
                                contentType: file.type || 'application/octet-stream'
                            },
                            // headers: {
                            //     Authorization: `Bearer ${uploadToken}`
                            // }
                        });

                        await putFileToOSS(data.data.url, file.originFileObj as RcFile,
                            (event) => updateProgress(event, fileUid));

                        setSsmFileList(prev =>
                            prev.map(f => f.uid === fileUid
                                ? { ...f, status: 'done', response: { ossKey: data.data.key } }
                                : f
                            )
                        );

                        return {
                            type: 'ssm',
                            uid: fileUid,
                            originalName,
                            ossKey: data.data.key
                        };

                    } catch (error) {
                        console.error(`Upload failed for ${originalName}:`, error);

                        setSsmFileList(prev =>
                            prev.map(f => f.uid === fileUid
                                ? { ...f, status: 'error', error: error as Error }
                                : f
                            )
                        );

                        return {
                            type: 'ssm',
                            uid: fileUid,
                            originalName,
                            error: error as Error
                        };
                    }
                };

                uploadTasks.push(task());
            }
        });

        // Prepare company electricity bill file upload task
        electricityBillFileList.forEach((file, index) => {
            if (file.originFileObj && file.status !== 'done') {
                const task = async (): Promise<UploadResult> => {
                    const fileUid = file.uid;
                    const originalName = file.name;

                    try {
                        const validationError = validateFile(file.originFileObj as RcFile);
                        if (validationError) {
                            throw new Error(validationError);
                        }

                        const { data } = await axios.get(`${apiBase}/presigned`, {
                            params: {
                                registerId: companyId,
                                fileType: 'general',
                                fileName: file.name,
                                contentType: file.type || 'application/octet-stream'
                            },
                            // headers: {
                            //     Authorization: `Bearer ${uploadToken}`
                            // }
                        });

                        await putFileToOSS(data.data.url, file.originFileObj as RcFile,
                            (event) => updateProgress(event, fileUid));

                        setElectricityBillFileList(prev =>
                            prev.map(f => f.uid === fileUid
                                ? { ...f, status: 'done', response: { ossKey: data.data.key } }
                                : f
                            )
                        );

                        return {
                            type: 'electricity',
                            uid: fileUid,
                            originalName,
                            ossKey: data.data.key
                        };

                    } catch (error) {
                        console.error(`Upload failed for ${originalName}:`, error);

                        setElectricityBillFileList(prev =>
                            prev.map(f => f.uid === fileUid
                                ? { ...f, status: 'error', error: error as Error }
                                : f
                            )
                        );

                        return {
                            type: 'electricity',
                            uid: fileUid,
                            originalName,
                            error: error as Error,
                        };
                    }
                };

                uploadTasks.push(task());
            }
        });

        // Prepare tenant agreement file upload task
        if (isTenant) {
            tenantAgreementFileList.forEach((file) => {
                if (file.originFileObj && file.status !== 'done') {
                    const task = async (): Promise<UploadResult> => {
                        const fileUid = file.uid;
                        const originalName = file.name;
                        try {
                            const validationError = validateFile(file.originFileObj as RcFile);
                            if (validationError) throw new Error(validationError);

                            const { data } = await axios.get(`${apiBase}/presigned`, {
                                params: {
                                    registerId: companyId,
                                    fileType: 'general',
                                    fileName: file.name,
                                    contentType: file.type || 'application/octet-stream',
                                    turnstileToken: turnstileToken
                                },
                                // headers: { Authorization: `Bearer ${uploadToken}` }
                            });

                            await putFileToOSS(data.data.url, file.originFileObj as RcFile,
                                (event) => updateProgress(event, fileUid));

                            setTenantAgreementFileList(prev =>
                                prev.map(f => f.uid === fileUid
                                    ? { ...f, status: 'done', response: { ossKey: data.data.key } }
                                    : f
                                )
                            );
                            
                            return { 
                                type: 'tenancy', 
                                uid: fileUid, 
                                originalName, 
                                ossKey: data.data.key 
                            };
                        } catch (error) {
                            setTenantAgreementFileList(prev =>
                                prev.map(f => f.uid === fileUid
                                    ? { ...f, status: 'error', error: error as Error }
                                    : f
                                )
                            );
                            return { 
                                type: 'tenancy', 
                                uid: fileUid, 
                                originalName, 
                                error: error as Error 
                            };
                        }
                    };

                    uploadTasks.push(task());
                }
            });
        }

        // Execute with concurrency control
        const results: UploadResult[] = [];
        const queue = [...uploadTasks];

        const worker = async () => {
            while (queue.length > 0) {
                const task = queue.shift();
                if (task) {
                    const result = await task;
                    results.push(result);
                }
            }
        };

        const workers = Array.from({ length: Math.min(config.concurrentUploads, uploadTasks.length) },
            () => worker()
        );

        await Promise.allSettled(workers);

        setUploadResults(results);

        return results;
    };

    // -- Get Upload Toke --
    const getUploadToken = async (turnstileToken: string): Promise<string> => {
        try {
            const response = await axios.post<UploadTokenResponse>(
                `${apiBase}/upload-token`,
                { turnstileToken }
            );

            if (response.data.success && response.data.data.uploadToken) {
                return response.data.data.uploadToken;
            }
            throw new Error('Failed to get upload token');
        } catch (error) {
            console.error("Failed to get upload token:", error);
            throw error;
        }
    };

    // --- Form Submission ---
    const onFinish = async (values: CompanyFormValues) => {
        // if (!turnstileToken) {
        //     messageApi.error({
        //         content: 'Please complete the Cloudflare Security Verification located at the bottom of the form.',
        //         icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
        //     });
        //     return;
        // }

        const currentPlateNumbers = values.plateNumbers?.filter(p => p && p.plateNumber.trim() !== '') || [];

        // Enhanced validation for all three documents per vehicle
        let hasAllVehicleDocs = true;
        currentPlateNumbers.forEach((_, index) => {
           const hasVehicleDoc = (plateFilesList[index]?.vehicleFile || []).length > 0;
            if (!hasVehicleDoc) {
                hasAllVehicleDocs = false;
                messageApi.error({
                    content: t(
                        "Please attach vehicle documents for Vehicle #{{number}} (SPA, Electric Bill, Vehicle Document)",
                        { number: index + 1 },
                    ),
                    icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                });
            }
        });

        if (!hasAllVehicleDocs)  return;

        if (ssmFileList.length === 0) {
            messageApi.error({
                content: t('Please attach SSM document.'),
                icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
            });
            return;
        }

        if (electricityBillFileList.length === 0) {
            messageApi.error({
                content: t('Please attach Company Electricity Bill.'),
                icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
            });
            return;
        }

        setUploading(true);
        setUploadProgress({});
        setUploadResults([]);

        // Lookup selected pass type option to extract the card type label
        const selectedPassOption = vehiclePassType.find(
            (opt) => opt.option_key === String(values.vehiclePassType)
        );
        const cardType = selectedPassOption?.option_value ?? '';

        try {
            // Step 1: Create Company
            messageApi.loading({
                content: 'Creating company record...',
                duration: 0,
                icon: <LoadingOutlined style={{ color: '#1890ff' }} />
            });
            const createResponse = await axios.post<CreateCompanyResponse>(
                `${apiBase}/registers/company`,
                {
                    companyRegistrationNumber: values.companyRegistrationNumber,
                    tinNumber: values.tinNumber,
                    companyName: values.companyName,
                    contactPerson: values.contactPerson,
                    contactNumber: values.contactNumber,
                    contactEmail: values.contactEmail,
                    companyAddressLine1: values.companyAddressLine1,
                    companyAddressLine2: values.companyAddressLine2,
                    companyPlates: currentPlateNumbers.map(p => ({
                        plateNumber: p.plateNumber,
                        vehicleType: p.vehicleType || '1',
                    })),
                    IDType: values.IDType,
                    IDNumber: values.IDNumber,
                    vehiclePassType: values.vehiclePassType,
                    cardType: cardType
                } as CreateCompanyRequest
            );

            const companyId = createResponse.data.data.employerID;
            messageApi.destroy();
            messageApi.success({
                content: `Company created (ID: ${companyId}). Starting uploads...`,
                icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />
            });

            // Step 2: Upload Files
            const results = await processUploadQueue(companyId, values);

            // Analyze results
            const successfulUploads = results.filter(r => r.ossKey && !r.error);
            const failedUploads = results.filter(r => r.error);

            console.log('Upload Results:', {
                total: results.length,
                successful: successfulUploads.length,
                failed: failedUploads.length,
                results
            });

            if (failedUploads.length > 0) {
                const failedNames = failedUploads.map(r => r.originalName).join(', ');
                throw new Error(`${failedUploads.length} file(s) failed to upload: ${failedNames}`);
            }

            if (successfulUploads.length !== results.length) {
                throw new Error('Not all files were successfully uploaded.');
            }

            messageApi.success({
                content: `All ${successfulUploads.length} files uploaded successfully!`,
                icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />
            });

            // Step 3: Prepare finalization data
            const companyPlates: PlateFileEntry[] = [];
            const companySupportingFiles: SupportingFileEntry = {
                ssmPath: '',
                electricBillPath: '',
                tenantAgreementPath: ''
            };

            // Group uploads by plate to collect all three document paths per vehicle
            const plateUploads: Record<string, {
                plateNumber: string;
                vehicleType: string;
                spaPath?: string;
                electricBillPath?: string;
                vehiclePath?: string;
            }> = {};

            successfulUploads.forEach(result => {
                if (result.type === 'ssm' && result.ossKey) {
                    companySupportingFiles.ssmPath = result.ossKey;
                } else if (result.type === 'electricity' && result.ossKey) {
                    companySupportingFiles.electricBillPath = result.ossKey;
                } else if (result.type === 'tenancy' && result.ossKey) {
                    companySupportingFiles.tenantAgreementPath = result.ossKey;
                } else if (result.type === 'plate' && result.ossKey && result.plateNumber && result.vehicleType) {
                    const plateKey = `${result.plateNumber}-${result.plateIndex}`;

                    if (!plateUploads[plateKey]) {
                        plateUploads[plateKey] = {
                            plateNumber: result.plateNumber,
                            vehicleType: result.vehicleType || '',
                            spaPath: '',
                            electricBillPath: '',
                            vehiclePath: ''
                        };
                    }

                    // Assign the OSS key to the correct field based on subType
                    if (result.subType === 'spa') {
                        plateUploads[plateKey].spaPath = result.ossKey;
                    } else if (result.subType === 'electricBill') {
                        plateUploads[plateKey].electricBillPath = result.ossKey;
                    } else if (result.subType === 'vehicle') {
                        plateUploads[plateKey].vehiclePath = result.ossKey;
                    }
                }
            });

            // Convert grouped uploads to companyPlates array
            Object.values(plateUploads).forEach(plateData => {
                companyPlates.push({
                    plateNumber: plateData.plateNumber,
                    vehicleType: plateData.vehicleType,
                    vehiclePath: plateData.vehiclePath || ''
                });
            });

            // Step 4: Finalize Company
            messageApi.loading({
                content: 'Finalizing company registration...',
                duration: 0,
                icon: <LoadingOutlined style={{ color: '#1890ff' }} />
            });
            const finalizeResponse = await axios.post<FinalizeCompanyResponse>(
                `${apiBase}/registers/company/finalize`,
                {
                    employerID: companyId,
                    companyRegistrationNumber: values.companyRegistrationNumber,
                    tinNumber: values.tinNumber,
                    companyName: values.companyName,
                    contactPerson: values.contactPerson,
                    contactNumber: values.contactNumber,
                    contactEmail: values.contactEmail,
                    companyAddressLine1: values.companyAddressLine1,
                    companyAddressLine2: values.companyAddressLine2 || '',
                    isTenant: isTenant,
                    companyPlates,
                    companySupportingFiles,
                    IDType: values.IDType,
                    IDNumber: values.IDNumber,
                    vehiclePassType: values.vehiclePassType,
                    cardType: cardType
                } as FinalizeCompanyRequest
            );

            messageApi.destroy();
            messageApi.success({
                content: t('Company {{id}} finalized successfully!', { id: finalizeResponse.data.id }),
                icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />
            });

            // Show success summary
            messageApi.success({
                content: t('Submission Complete'),
                icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />
            });

            // Reset form
            form.resetFields();
            setSsmFileList([]);
            setElectricityBillFileList([]);
            setTenantAgreementFileList([]);
            setIsTenant(null);
            setPlateFilesList({});
            setUploadProgress({});
            setUploadResults([]);
            setTurnstileToken(null);
            turnstileRef.current?.reset();

        } catch (error) {
            // Reset file statuses so they re-upload on retry with new companyId
            setSsmFileList(prev => prev.map(({ status, ...f }) => f as CustomUploadFile));
            setElectricityBillFileList(prev => prev.map(({ status, ...f }) => f as CustomUploadFile));
            setPlateFilesList(prev => {
                const reset: PlateFileList = {};
                Object.entries(prev).forEach(([key, entry]) => {
                    reset[Number(key)] = {
                        vehicleFile: entry.vehicleFile?.map(({ status, ...f }) => f as CustomUploadFile),
                    };
                });
                return reset;
            });

            console.error('Submission failed:', error);
            messageApi.destroy();
            const submissionFailure = (details: string) => `${t('Submission failed')}: ${details}`;

            if (axios.isAxiosError(error)) {
                const platesWithErrors = error.response?.data?.errors?.plates;

                if (Array.isArray(platesWithErrors) && platesWithErrors.length > 0) {
                    const currentPlateNumbers = form.getFieldValue('plateNumbers') || [];
                    const formErrors: any[] = [];

                    platesWithErrors.forEach((plateStr: string) => {
                        currentPlateNumbers.forEach((p: any, index: number) => {
                            if (p && p.plateNumber === plateStr) {
                                formErrors.push({
                                    name: ['plateNumbers', index, 'plateNumber'],
                                    errors: ['Vehicle number is already registered']
                                });
                            }
                        });
                    });

                    if (formErrors.length > 0) {
                        form.setFields(formErrors);
                        form.scrollToField(formErrors[0].name, { behavior: 'smooth', block: 'center' });
                    }
                    messageApi.error({
                        content: submissionFailure(
                            error.response?.data?.errors?.message || t('Some vehicle numbers are already registered'),
                        ),
                        icon: <ExclamationCircleOutlined style={{ color: '#ff80ab' }} />
                    });
                } else if (error.response?.data?.errors?.errors) {
                    const fieldErrors = error.response.data.errors.errors;
                    const errorMessages = fieldErrors.map((err: any) => `${err.field}: ${err.message}`);
                    messageApi.error({
                        content: submissionFailure(`Validation failed: ${errorMessages.join(', ')}`),
                        icon: <ExclamationCircleOutlined style={{ color: '#ff80ab' }} />,
                        duration: 10
                    });
                } else {
                    messageApi.error({
                        content: submissionFailure(
                            error.response?.data?.message || error.response?.data?.error || error.message,
                        ),
                        icon: <ExclamationCircleOutlined style={{ color: '#ff80ab' }} />
                    });
                }
            } else if (error instanceof Error) {
                messageApi.error({
                    content: submissionFailure(error.message),
                    icon: <ExclamationCircleOutlined style={{ color: '#ff80ab' }} />
                });
            } else {
                messageApi.error({
                    content: submissionFailure(t('An unknown error occurred')),
                    icon: <ExclamationCircleOutlined style={{ color: '#ff80ab' }} />
                });
            }
        } finally {
            setUploading(false);
        }
    };

    const onFinishFailed = (errorInfo: any) => {
        console.log('Failed:', errorInfo);
        messageApi.error({
            content: t('Please check all fields. Some required information or documents are missing.'),
            duration: 10,
            icon: <ExclamationCircleOutlined style={{ color: '#ff80ab' }} />
        })

        if (errorInfo.errorFields.length > 0) {
            const firstErrorField = errorInfo.errorFields[0].name;
            form.scrollToField(firstErrorField, { behavior: 'smooth', block: 'center' });
        }
    };

    // Show loading state while config is being fetched
    if (configLoading || vehiclePassTypesLoading || idTypeLoading || fieldConfigLoading) {
        return (
            <Layout className="page-layout" style={{ minHeight: '100vh' }}>
                <Content style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Card style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                        <div style={{ fontSize: '24px', marginBottom: '16px' }}>{t("Loading...")}</div>
                    </Card>
                </Content>
            </Layout>
        );
    }

    // Show registration closed message
    if (!config.enableRegistration) {
        return (
            <Layout
                style={{
                    minHeight: '100vh',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
            >
                <Content
                    style={{
                        padding: responsiveConfig.layout.containerPadding,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'auto',
                    }}
                >
                    <div
                        style={{
                            width: '100%',
                            maxWidth: responsiveConfig.layout.maxWidth,
                            margin: '0 auto',
                        }}
                    >
                        <Card
                            style={{
                                width: '100%',
                                background: 'white',
                                borderRadius: screens.xs ? '12px' : '16px',
                                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                                padding: responsiveConfig.layout.cardPadding,
                            }}
                        >
                            <Title level={3} style={{ textAlign: 'center' }}>{t("Registration Closed")}</Title>
                            <Text style={{ display: 'block', textAlign: 'center' }}>{t("Company registration is currently disabled.")}</Text>
                        </Card>
                    </div>
                </Content>
            </Layout>
        );
    }

    return (
        <Layout className="page-layout" style={{ minHeight: '100vh' }}>
            <Content
                style={{
                    padding: responsiveConfig.layout.containerPadding,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'auto',
                }}
            >
                <div
                    style={{
                        width: '100%',
                        maxWidth: responsiveConfig.layout.maxWidth,
                        margin: '0 auto',
                    }}
                >
                    {contextHolder}
                    <Card
                        style={{
                            width: '100%',
                            // background: 'white',
                            borderRadius: screens.xs ? '12px' : '16px',
                            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                            padding: responsiveConfig.layout.cardPadding,
                        }}
                    >
                        {/* Back Button */}
                        <div style={{ textAlign: 'left', marginBottom: screens.xs ? 16 : 24 }}>
                            <Button
                                icon={<ArrowLeftOutlined />}
                                onClick={() => window.history.back()}
                                type="default"
                                size={screens.xs ? 'middle' : 'large'}
                                style={{
                                    borderRadius: '8px',
                                }}
                            >
                                {t("Back")}
                            </Button>
                        </div>

                        {/* Title */}
                        <Title
                            level={2}
                            style={{
                                textAlign: 'center',
                                marginBottom: screens.xs ? 24 : 32,
                                fontSize: responsiveConfig.typography.titleSize,
                                color: '#1890ff'
                            }}
                        >
                            {t("Company Registration")}
                        </Title>

                        {/* Main Form */}
                        <Form
                            form={form}
                            onFinish={onFinish}
                            onFinishFailed={onFinishFailed}
                            disabled={uploading}
                            layout="vertical"
                            initialValues={{
                                consentPDPA: false,
                                consentTOC: false, 
                                plateNumbers: [{ plateNumber: '', vehicleType: '1' }] 
                            }}
                        >
                            {/* Company Details */}
                            <Row gutter={responsiveConfig.layout.gutter}>
                                {isFieldEnabled('companyName') &&
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="companyName"
                                        label={
                                            <span>
                                                {t("Property Owner Name")}{' '}
                                                <Tooltip title="Enter the full name of the property owner">
                                                    <InfoCircleOutlined
                                                        style={{ color: '#1890ff', cursor: 'pointer' }}
                                                        onClick={() => setModalConfig({
                                                            open: true,
                                                            title: 'Property Owner Information',
                                                            content: <p>Enter the full name of the property owner.</p>,
                                                        })}
                                                    />
                                                </Tooltip>
                                            </span>
                                        }
                                        rules={[
                                            { required: true, message: t("Required") },
                                            { min: 1, message: t("Minimum 1 characters") },
                                            { max: 64, message: t("Maximum 64 characters") },
                                        ]}
                                    >
                                        <Input placeholder={t("Enter Property Owner Name")} size="large" maxLength={64} />
                                    </Form.Item>
                                </Col>
                                }

                                {false &&
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="contactPerson"
                                        label={
                                            <span>
                                                Contact Person{' '}
                                                <Tooltip title="Enter the name of the primary contact person">
                                                    <InfoCircleOutlined
                                                        style={{ color: '#1890ff', cursor: 'pointer' }}
                                                        onClick={() => setModalConfig({
                                                            open: true,
                                                            title: 'Contact Person Information',
                                                            content: <p>Enter the name of the individual who will be the primary point of contact for this registration.</p>,
                                                        })}
                                                    />
                                                </Tooltip>
                                            </span>
                                        }
                                        rules={[
                                            { required: true, message: t("Required") },
                                            { min: 5, message: t("Minimum 5 characters") },
                                            { max: 64, message: t("Maximum 64 characters") },
                                        ]}
                                    >
                                        <Input placeholder="Enter Contact Person" size="large" maxLength={64} />
                                    </Form.Item>
                                </Col>
                                }
                            </Row>
                            
                            <Row gutter={responsiveConfig.layout.gutter}>
                                {isFieldEnabled('IDType') &&
                                <Col xs={24} md={12}>
                                     <Form.Item
                                        name="IDType"
                                        label={
                                            <span>
                                                {t("ID Type")}{' '}
                                                <Tooltip title="Select the applicable ID type">
                                                    <InfoCircleOutlined
                                                        style={{ color: '#1890ff', cursor: 'pointer' }}
                                                        onClick={() => setModalConfig({
                                                            open: true,
                                                            title: 'ID Type',
                                                            content: <p>Select the ID type that applies to this resident.</p>,
                                                        })}
                                                    />
                                                </Tooltip>
                                            </span>
                                        }
                                        rules={[{ required: true, message: t("Please select a ID type") }]}
                                    >
                                        <Select
                                            placeholder={t("Select ID Type")}
                                            size="large"
                                            loading={idTypeLoading}
                                            options={idType
                                                .filter(option => option.is_active)
                                                .sort((a, b) => a.display_order - b.display_order)
                                                .map(option => ({
                                                    value: option.option_key,
                                                    label: t(option.option_value),
                                                }))}
                                        />
                                    </Form.Item>
                                </Col>
                                }

                                {isFieldEnabled('IDNumber') &&
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="IDNumber"
                                        label={
                                            <span>
                                                {t("ID Number")}{' '}
                                                <Tooltip title="Enter your National Registration Identity Card number or Passport number">
                                                    <InfoCircleOutlined
                                                        style={{ color: '#1890ff', cursor: 'pointer' }}
                                                        onClick={() => setModalConfig({
                                                            open: true,
                                                            title: 'ID Number',
                                                            content: <p>Enter your National Registration Identity Card number or Passport number for identification purposes.</p>,
                                                        })}
                                                    />
                                                </Tooltip>
                                            </span>
                                        }
                                        required
                                        rules={[
                                            { required: true, message: t("Please enter your ID Number") },
                                            { min: 5, message: t("Minimum 5 character") },
                                            { max: 16, message: t("Maximum 16 characters") },
                                        ]}
                                    >
                                        <Input
                                            placeholder={t("Enter ID Number")}
                                            size="large"
                                            maxLength={16}
                                        />
                                    </Form.Item>
                                </Col>
                                }
                            </Row>

                            <Row gutter={responsiveConfig.layout.gutter}>
                                {isFieldEnabled('contactNumber') &&
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="contactNumber"
                                        label={
                                            <span>
                                                {t("Contact Number")}{' '}
                                                <Tooltip title="Enter the contact number for your company">
                                                    <InfoCircleOutlined
                                                        style={{ color: '#1890ff', cursor: 'pointer' }}
                                                        onClick={() => setModalConfig({
                                                            open: true,
                                                            title: 'Contact Number Information',
                                                            content: <p>Provide a valid contact number (mobile or office) for your company.</p>,
                                                        })}
                                                    />
                                                </Tooltip>
                                            </span>
                                        }
                                        rules={[
                                            { required: true, message: t("Required") },
                                            { min: 5, message: t("Minimum 5 characters") },
                                            { max: 12, message: t("Maximum 12 characters") },
                                        ]}
                                    >
                                        <Input placeholder={t("Enter Contact Number")} size="large" maxLength={12} />
                                    </Form.Item>
                                </Col>
                                }

                                {isFieldEnabled('contactEmail') &&
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="contactEmail"
                                        label={
                                            <span>
                                                {t("Email Address")}{' '}
                                                <Tooltip title="Enter the company's primary contact email">
                                                    <InfoCircleOutlined
                                                        style={{ color: '#1890ff', cursor: 'pointer' }}
                                                        onClick={() => setModalConfig({
                                                            open: true,
                                                            title: 'Email Address Information',
                                                            content: <p>Provide a valid official email address for receiving updates and notifications.</p>,
                                                        })}
                                                    />
                                                </Tooltip>
                                            </span>
                                        }
                                        rules={[
                                            { required: true, message: t("Required") },
                                            { min: 5, message: t("Minimum 5 characters") },
                                            { max: 32, message: t("Maximum 32 characters") },
                                            { type: 'email', message: t("Not valid email address") }
                                        ]}
                                    >
                                        <Input placeholder={t("Enter Email Address")} size="large" maxLength={32} />
                                    </Form.Item>
                                </Col>
                                }
                            </Row>

                            <Row gutter={responsiveConfig.layout.gutter}>
                                {isFieldEnabled('companyAddressLine1') &&
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="companyAddressLine1"
                                        label={
                                            <span>
                                                {t("Company Address Line 1")}{' '}
                                                <Tooltip title="Enter the first line of your company's registered address">
                                                    <InfoCircleOutlined
                                                        style={{ color: '#1890ff', cursor: 'pointer' }}
                                                        onClick={() => setModalConfig({
                                                            open: true,
                                                            title: 'Address Line 1 Information',
                                                            content: <p>Enter the first line of your registered business address (e.g., Unit Number, Building Name).</p>,
                                                        })}
                                                    />
                                                </Tooltip>
                                            </span>
                                        }
                                        rules={[
                                            { required: true, message: t("Required") },
                                            { min: 5, message: t("Minimum 5 characters") },
                                            { max: 48, message: t("Maximum 48 characters") }
                                        ]}
                                    >
                                        <Input
                                            placeholder={t("Company Address Line 1")}
                                            size="large"
                                            maxLength={48}
                                        />
                                    </Form.Item>
                                </Col>
                                }

                                {isFieldEnabled('companyAddressLine2') &&
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="companyAddressLine2"
                                        label={
                                            <span>
                                                {t("Company Address Line 2")}{' '}
                                                <Tooltip title="Enter the second line of your company's address (optional)">
                                                    <InfoCircleOutlined
                                                        style={{ color: '#1890ff', cursor: 'pointer' }}
                                                        onClick={() => setModalConfig({
                                                            open: true,
                                                            title: 'Address Line 2 Information',
                                                            content: <p>Enter the second line of your registered business address (e.g., Road Name, Area/District).</p>,
                                                        })}
                                                    />
                                                </Tooltip>
                                            </span>
                                        }
                                        rules={[
                                            { required: false },
                                            { min: 5, message: t("Minimum 5 characters") },
                                            { max: 48, message: t("Maximum 48 characters") }
                                        ]}
                                    >
                                        <Input
                                            placeholder={t("Company Address Line 2")}
                                            size="large"
                                            maxLength={48}
                                        />
                                    </Form.Item>
                                </Col>
                                }
                            </Row>

                            <Row gutter={responsiveConfig.layout.gutter}>
                                {isFieldEnabled('tinNumber') &&
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="tinNumber"
                                        label={
                                            <span>
                                                {t("Tax Identification Number (TIN)")}{' '}
                                                <Tooltip title="Enter your company's Tax Identification Number">
                                                    <InfoCircleOutlined
                                                        style={{ color: '#1890ff', cursor: 'pointer' }}
                                                        onClick={() => setModalConfig({
                                                            open: true,
                                                            title: 'TIN Information',
                                                            content: <p>Enter the Tax Identification Number (TIN) assigned to your company for tax identification purposes.</p>,
                                                        })}
                                                    />
                                                </Tooltip>
                                            </span>
                                        }
                                        rules={[
                                            { required: true, message: t("Required") },
                                            { min: 5, message: t("Minimum 5 characters") },
                                            { max: 16, message: t("Maximum 16 characters") },
                                        ]}
                                    >
                                        <Input
                                            placeholder={t("Enter TIN")}
                                            size="large"
                                            maxLength={16}
                                        />
                                    </Form.Item>
                                </Col>
                                }

                                {isFieldEnabled('vehiclePassType') &&
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="vehiclePassType"
                                        label={
                                            <span>
                                                {t("Vehicle Pass Type")}{' '}
                                                <Tooltip title="Select the applicable vehicle pass type">
                                                    <InfoCircleOutlined
                                                        style={{ color: '#1890ff', cursor: 'pointer' }}
                                                        onClick={() => setModalConfig({
                                                            open: true,
                                                            title: 'Vehicle Pass Type',
                                                            content: <p>Select the vehicle pass type that applies to this resident.</p>,
                                                        })}
                                                    />
                                                </Tooltip>
                                            </span>
                                        }
                                        rules={[{ required: true, message: t("Please select a vehicle pass type") }]}
                                    >
                                        <Select
                                            placeholder={t("Select Vehicle Pass Type")}
                                            size="large"
                                            loading={vehiclePassTypesLoading}
                                            options={vehiclePassType
                                                .filter(option => option.is_active)
                                                .sort((a, b) => a.display_order - b.display_order)
                                                .map(option => ({
                                                    value: option.option_key,
                                                    label: t(option.option_value),
                                                }))}
                                        />
                                    </Form.Item>
                                </Col>
                                }
                            </Row>

                            {(isFieldEnabled('plateNumber') || isFieldEnabled('vehicleType') || isFieldEnabled('vehicleFile')) &&
                            <Divider style={{ margin: '24px 0' }}>
                                {t("Vehicle Information")}
                            </Divider>
                            }

                            {/* Dynamic Plate List with Three Document Uploads per Vehicle */}
                            <Form.List name="plateNumbers">
                                {(fields, { add, remove }) => (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                        {fields.map(({ key, name, ...restField }, index) => (
                                            <Card
                                                key={key}
                                                size="small"
                                                type="inner"
                                                title={t("Vehicle #{{number}}", { number: index + 1 })}
                                                extra={fields.length > 1 ? (
                                                    <Button
                                                        type="text"
                                                        danger
                                                        icon={<MinusCircleOutlined />}
                                                        onClick={() => {
                                                            remove(name);
                                                            setPlateFilesList(prev => {
                                                                const copy = { ...prev };
                                                                delete copy[index];
                                                                return copy;
                                                            });
                                                        }}
                                                    />
                                                ) : null}
                                            >
                                                <Row gutter={responsiveConfig.layout.gutter}>
                                                    {isFieldEnabled('plateNumber') &&
                                                    <Col xs={24} md={6}>
                                                        <Form.Item
                                                            {...restField}
                                                            name={[name, 'plateNumber']}
                                                            label={
                                                                <span>
                                                                    {t("Plate Number")}{' '}
                                                                    <Tooltip title="Enter the staff vehicle plate number">
                                                                        <InfoCircleOutlined
                                                                            style={{ color: '#1890ff', cursor: 'pointer' }}
                                                                            onClick={() => setModalConfig({
                                                                                open: true,
                                                                                title: 'Plate Number Information',
                                                                                content: <p>Enter the registration plate number of the staff vehicle (e.g., ABC1234).</p>,
                                                                            })}
                                                                        />
                                                                    </Tooltip>
                                                                </span>
                                                            }
                                                            rules={[
                                                                { required: true, message: t("Required") },
                                                                { max: 20, message: t("Maximum 20 characters") }
                                                            ]}
                                                        >
                                                            <Input
                                                                placeholder={t("Enter Plate Number")}
                                                                size="large"
                                                                maxLength={20}
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                    }
                                                    {isFieldEnabled('vehicleType') &&
                                                    <Col xs={24} md={6}>
                                                        <Form.Item
                                                            {...restField}
                                                            name={[name, 'vehicleType']}
                                                            label={
                                                                <span>
                                                                    {t("Vehicle Type")}{' '}
                                                                    <Tooltip title="Select the class of the vehicle">
                                                                        <InfoCircleOutlined
                                                                            style={{ color: '#1890ff', cursor: 'pointer' }}
                                                                            onClick={() => setModalConfig({
                                                                                open: true,
                                                                                title: 'Vehicle Type Information',
                                                                                content: <p>Select the appropriate category for the staff vehicle. Refer to the identification guide if unsure.</p>,
                                                                                image: vehicleClassesImg
                                                                            })}
                                                                        />
                                                                    </Tooltip>
                                                                </span>
                                                            }
                                                            rules={[{ required: true, message: t("Required") }]}
                                                        >
                                                            <Select
                                                                placeholder={t("Select Vehicle Class")}
                                                                size="large"
                                                                options={vehicleOptions.map(option => ({
                                                                    ...option,
                                                                    label: t(option.label),
                                                                }))}
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                    }
                                                    {isFieldEnabled('vehicleFile') &&
                                                    <Col xs={24} md={8}>
                                                        <Form.Item
                                                            label={
                                                                <span>
                                                                    {t("Vehicle Ownership Certificate (VOC)")}{' '}
                                                                    <Tooltip title="Upload the vehicle registration document">
                                                                        <InfoCircleOutlined
                                                                            style={{ color: '#1890ff', cursor: 'pointer' }}
                                                                            onClick={() => setModalConfig({
                                                                                open: true,
                                                                                title: 'Vehicle Document Information',
                                                                                content: <p>Upload a clear copy of the vehicle's registration document (Geran/VOC).</p>,
                                                                            })}
                                                                        />
                                                                    </Tooltip>
                                                                </span>
                                                            }
                                                            {...restField}
                                                            name={[name, 'vehicleDoc']}
                                                            required
                                                            rules={[
                                                                {
                                                                    validator: async () => {
                                                                        if ((plateFilesList[index]?.vehicleFile || []).length === 0) {
                                                                            return Promise.reject(new Error(t("Required")));
                                                                        }
                                                                    }
                                                                }
                                                            ]}
                                                        >
                                                            <Upload
                                                                fileList={plateFilesList[index]?.vehicleFile || []}
                                                                onChange={handleVehicleFileChange(index)}
                                                                beforeUpload={(file) => {
                                                                    const validationError = validateFile(file);
                                                                    if (validationError) {
                                                                        messageApi.error({
                                                                            content: validationError,
                                                                            icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                                                                        });
                                                                        return Upload.LIST_IGNORE;
                                                                    }
                                                                    return false;
                                                                }}
                                                                customRequest={dummyRequest}
                                                                maxCount={1}
                                                                disabled={uploading}
                                                                showUploadList={{
                                                                    showRemoveIcon: true,
                                                                    removeIcon: <DeleteOutlined style={{ color: '#ff4d4f' }} />,
                                                                }}
                                                                iconRender={() => <PaperClipOutlined  style={{ color: '#ffffff' }} />}
                                                            >
                                                                <Button
                                                                    size="large"
                                                                    icon={<UploadOutlined />}
                                                                    disabled={uploading}
                                                                    style={{ width: '100%' }}
                                                                >
                                                                    {t("Vehicle Doc")}
                                                                </Button>
                                                            </Upload>
                                                        </Form.Item>
                                                    </Col>
                                                    }
                                                </Row>

                                                {/* Progress indicators for each file type */}
                                                {uploading && (
                                                    <>
                                                        {(plateFilesList[index]?.vehicleFile || []).map(file => (
                                                            uploadProgress[file.uid] !== undefined && (
                                                                <div key={`vehicle-${file.uid}`} style={{ marginTop: 8 }}>
                                                                    <Text>{t("Vehicle Doc")}: {file.name}</Text>
                                                                    <Progress
                                                                        percent={uploadProgress[file.uid]}
                                                                        size="small"
                                                                        status={file.status === 'error' ? 'exception' : 'active'}
                                                                    />
                                                                </div>
                                                            )
                                                        ))}
                                                    </>
                                                )}
                                            </Card>
                                        ))}

                                        <Button
                                            type="dashed"
                                            onClick={() => add({ plateNumber: '', vehicleType: '1' })}
                                            block
                                            icon={<PlusOutlined />}
                                            disabled={fields.length >= config.maxPlateNumbers || uploading}
                                        >
                                            {t("Add Another Vehicle")}
                                        </Button>
                                    </div>
                                )}
                            </Form.List>

                            {(isFieldEnabled('ssmFile') || isFieldEnabled('electricityFiles')) &&
                            <Divider style={{ margin: '24px 0' }}>
                                {t("Supporting Documents")}
                            </Divider>
                            }

                            {/* SSM Files Upload */}
                            {isFieldEnabled('ssmFile') &&
                            <Form.Item
                                label={
                                    <span>
                                        {t("SPA/Quit Rent Bill/Assessment Bill")}{' '}
                                        <Tooltip title="Upload your company's SPA/Quit Rent Bill/Assessment Bill">
                                            <InfoCircleOutlined
                                                style={{ color: '#1890ff', cursor: 'pointer' }}
                                                onClick={() => setModalConfig({
                                                    open: true,
                                                    title: 'Sale & Purchase Agreement (SPA) OR Quit Rent Bill OR Assessment Bill Information',
                                                    content: <p>Upload a clear copy of your company's Sale & Purchase Agreement (SPA)/Quit Rent Bill/Assessment Bill.</p>,
                                                })}
                                            />
                                        </Tooltip>
                                    </span>
                                }
                                name="ssmFile"
                                required
                                rules={[
                                    {
                                        validator: async () => {
                                            if (ssmFileList.length === 0) {
                                                return Promise.reject(new Error(t("Required")));
                                            }
                                        }
                                    }
                                ]}
                                extra={t("Upload SPA/Quit Rent Bill/Assessment Bill Agreement. Allowed: PDF, JPG, PNG. Max size: {{maxSize}} MB", {
                                    maxSize: config.maxFileSizeMB,
                                })}
                            >
                                <Dragger
                                    name="ssmFile"
                                    maxCount={config.maxGeneralFiles}
                                    fileList={ssmFileList}
                                    onChange={handleSsmFileChange}
                                    beforeUpload={(file) => {
                                        const validationError = validateFile(file);
                                        if (validationError) {
                                            messageApi.error({
                                                content: validationError,
                                                icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                                            });
                                            return Upload.LIST_IGNORE;
                                        }
                                        return false;
                                    }}
                                    customRequest={dummyRequest}
                                    listType={screens.xs ? "text" : "picture"}
                                    disabled={uploading}
                                    style={{
                                        borderRadius: '8px',
                                        background: '#fafafa'
                                    }}
                                    showUploadList={{
                                        showRemoveIcon: true,
                                        removeIcon: <DeleteOutlined style={{ color: '#ff4d4f' }} />,
                                    }}
                                    iconRender={() => <PaperClipOutlined style={{ color: '#ffffff' }} />}
                                >
                                    <p className="ant-upload-drag-icon">
                                        <InboxOutlined style={{ color: '#1890ff' }} />
                                    </p>
                                    <p className="ant-upload-text">
                                        {t("Click or drag file to upload SPA/Quit Rent Bill/Assessment Bill Agreement Document")}
                                    </p>
                                </Dragger>

                                {/* Progress for SSM files */}
                                {uploading && ssmFileList.map(file => (
                                    uploadProgress[file.uid] !== undefined && (
                                        <div key={file.uid} style={{ marginTop: 12 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Text style={{ fontSize: screens.xs ? '12px' : '14px' }}>
                                                    {file.name}
                                                    {file.status === 'done' && (
                                                        <Tag color="success" style={{ marginLeft: 8 }}>{t("Uploaded")}</Tag>
                                                    )}
                                                </Text>
                                                <Text type="secondary" style={{ fontSize: screens.xs ? '12px' : '14px' }}>
                                                    {uploadProgress[file.uid]}%
                                                </Text>
                                            </div>
                                            <Progress
                                                percent={uploadProgress[file.uid]}
                                                size="small"
                                                status={file.status === 'error' ? 'exception' : 'active'}
                                            />
                                        </div>
                                    )
                                ))}
                            </Form.Item>
                            }

                            {/* Company Electricity Bills Upload */}
                            {isFieldEnabled('electricityFiles') &&
                            <Form.Item
                                label={
                                    <span>
                                        {t("Company Electricity Bills")}{' '}
                                        <Tooltip title="Upload your company's electricity bill">
                                            <InfoCircleOutlined
                                                style={{ color: '#1890ff', cursor: 'pointer' }}
                                                onClick={() => setModalConfig({
                                                    open: true,
                                                    title: 'Electricity Bill Information',
                                                    content: <p>Upload a copy of the latest electricity bill for your company address.</p>,
                                                })}
                                            />
                                        </Tooltip>
                                    </span>
                                }
                                name="electricityFiles"
                                required
                                rules={[
                                    {
                                        validator: async () => {
                                            if (electricityBillFileList.length === 0) {
                                                return Promise.reject(new Error(t("Required")));
                                            }
                                        }
                                    }
                                ]}
                                extra={t("Upload Company Electricity Bill. Allowed: PDF, JPG, PNG. Max size: {{maxSize}} MB", {
                                    maxSize: config.maxFileSizeMB,
                                })}
                            >
                                <Dragger
                                    name="electricityFiles"
                                    maxCount={config.maxGeneralFiles}
                                    fileList={electricityBillFileList}
                                    onChange={handleElectricityBillFileChange}
                                    beforeUpload={(file) => {
                                        const validationError = validateFile(file);
                                        if (validationError) {
                                            messageApi.error({
                                                content: validationError,
                                                icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                                            });
                                            return Upload.LIST_IGNORE;
                                        }
                                        return false;
                                    }}
                                    customRequest={dummyRequest}
                                    listType={screens.xs ? "text" : "picture"}
                                    disabled={uploading}
                                    style={{
                                        borderRadius: '8px',
                                        background: '#fafafa'
                                    }}
                                    showUploadList={{
                                        showRemoveIcon: true,
                                        removeIcon: <DeleteOutlined style={{ color: '#ff4d4f' }} />,
                                    }}
                                    iconRender={() => <PaperClipOutlined style={{ color: '#ffffff' }} />}
                                >
                                    <p className="ant-upload-drag-icon">
                                        <InboxOutlined style={{ color: '#1890ff' }} />
                                    </p>
                                    <p className="ant-upload-text">
                                        {t("Click or drag company electricity bill files to this area")}
                                    </p>
                                </Dragger>

                                {/* Progress for Electricity Bill files */}
                                {uploading && electricityBillFileList.map(file => (
                                    uploadProgress[file.uid] !== undefined && (
                                        <div key={file.uid} style={{ marginTop: 12 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Text style={{ fontSize: screens.xs ? '12px' : '14px' }}>
                                                    {file.name}
                                                    {file.status === 'done' && (
                                                        <Tag color="success" style={{ marginLeft: 8 }}>{t("Uploaded")}</Tag>
                                                    )}
                                                </Text>
                                                <Text type="secondary" style={{ fontSize: screens.xs ? '12px' : '14px' }}>
                                                    {uploadProgress[file.uid]}%
                                                </Text>
                                            </div>
                                            <Progress
                                                percent={uploadProgress[file.uid]}
                                                size="small"
                                                status={file.status === 'error' ? 'exception' : 'active'}
                                            />
                                        </div>
                                    )
                                ))}
                            </Form.Item>
                            }

                            {(isFieldEnabled('tenantInformation') || isFieldEnabled('tenantFile')) &&
                            <Divider style={{ margin: '24px 0' }}>
                                {t("Tenant Details (if applicable)")}
                            </Divider>
                            }

                            {isFieldEnabled('tenantInformation') &&
                            <Form.Item 
                                name="tenantInformation"
                                label={t("Are you registering on behalf of your Tenant?")}
                                rules={[{ required: true, message: t("Please select an option") }]}
                            >
                                <Radio.Group onChange={(e) => {
                                    setIsTenant(e.target.value);
                                    if (!e.target.value) setTenantAgreementFileList([]);
                                }}>
                                    <Radio.Button value={true}>{t("Yes")}</Radio.Button>
                                    <Radio.Button value={false}>{t("No")}</Radio.Button>
                                </Radio.Group>
                            </Form.Item>
                            }

                            {isTenant && isFieldEnabled('tenantFile') && (
                                <Form.Item
                                    name="tenantInformation"
                                    label={
                                        <span>
                                            {t("Additional Document for Tenant")}{' '}
                                            <Tooltip title="Upload your latest tenancy agreement">
                                                <InfoCircleOutlined
                                                    style={{ color: '#1890ff', cursor: 'pointer' }}
                                                    onClick={() => setModalConfig({
                                                        open: true,
                                                        title: 'Tenancy Agreement Information',
                                                        content: <p>Upload a copy of your tenancy agreement to verify your tenant status.</p>,
                                                    })}
                                                />
                                            </Tooltip>
                                        </span>
                                    }
                                    rules={[{
                                        validator: async () => {
                                            if (tenantAgreementFileList.length === 0)
                                                return Promise.reject(new Error(t("Required")));
                                        }
                                    }]}
                                    extra={t("Upload Tenancy Agreement. Allowed: PDF, JPG, PNG. Max size: {{maxSize}} MB", {
                                        maxSize: config.maxFileSizeMB,
                                    })}
                                >
                                    <Dragger
                                        name="tenantDocFile"
                                        maxCount={config.maxGeneralFiles}
                                        fileList={tenantAgreementFileList}
                                        onChange={handleTenantAgreementFileChange}
                                        beforeUpload={(file) => {
                                            const validationError = validateFile(file);
                                            if (validationError) {
                                                messageApi.error({
                                                    content: validationError,
                                                    icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                                                });
                                                return Upload.LIST_IGNORE;
                                            }
                                            return false;
                                        }}
                                        customRequest={dummyRequest}
                                        listType={screens.xs ? "text" : "picture"}
                                        disabled={uploading}
                                        style={{ borderRadius: '8px', background: '#fafafa' }}
                                        showUploadList={{
                                            showRemoveIcon: true,
                                            removeIcon: <DeleteOutlined style={{ color: '#ff4d4f' }} />,
                                        }}
                                        iconRender={() => <PaperClipOutlined style={{ color: '#ffffff' }} />}
                                    >
                                        <p className="ant-upload-drag-icon">
                                            <InboxOutlined style={{ color: '#1890ff' }} />
                                        </p>
                                        <p className="ant-upload-text">
                                            {t("Click or drag file to upload Tenancy Agreement")}
                                        </p>
                                    </Dragger>

                                    {uploading && tenantAgreementFileList.map(file => (
                                        uploadProgress[file.uid] !== undefined && (
                                            <div key={file.uid} style={{ marginTop: 12 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <span>{file.name}</span>
                                                <span>{uploadProgress[file.uid]}%</span>
                                            </div>
                                            <Progress
                                                percent={uploadProgress[file.uid]}
                                                size="small"
                                                status={file.status === 'error' ? 'exception' : 'active'}
                                            />
                                            </div>
                                        )
                                    ))}
                                </Form.Item>
                            )}

                            {/* Terms and Conditions */}
                            <Divider style={{ margin: '24px 0' }}>
                                {t("Terms and Conditions")}
                            </Divider>

                            <Form.Item
                                name="consentPDPA"
                                valuePropName="checked"
                                rules={[
                                    {
                                        validator: (_, value) =>
                                            value ? Promise.resolve() : Promise.reject(new Error(t("You must consent to data processing"))),
                                    },
                                ]}
                                style={{ textAlign: 'left' }}
                            >
                                <Checkbox style={{ fontSize: responsiveConfig.typography.textSize, lineHeight: '1.5', whiteSpace: 'normal' }}>
                                    <Trans
                                        i18nKey="pdpaConsent"
                                        components={{
                                            pdpaLink: config.pdpaFileUrl ? (
                                                <a href={config.pdpaFileUrl} target="_blank" rel="noopener noreferrer" />
                                            ) : (
                                                <span />
                                            ),
                                        }}
                                    />
                                </Checkbox>
                            </Form.Item>

                            <Form.Item
                                name="consentTOC"
                                valuePropName="checked"
                                rules={[
                                    {
                                        validator: (_, value) =>
                                            value ? Promise.resolve() : Promise.reject(new Error(t("You must accept the Terms and Conditions"))),
                                    },
                                ]}
                                style={{ textAlign: 'left' }}
                            >
                                <Checkbox style={{ fontSize: responsiveConfig.typography.textSize, lineHeight: '1.5', whiteSpace: 'normal' }}>
                                    <Trans
                                        i18nKey="termsConsent"
                                        components={{
                                            termsLink: config.tncFileUrl ? (
                                                <a href={config.tncFileUrl} target="_blank" rel="noopener noreferrer" />
                                            ) : (
                                                <span />
                                            ),
                                        }}
                                    />
                                </Checkbox>
                            </Form.Item>

                            {/* <Form.Item style={{ marginTop: 24 }}>
                                <div style={{ textAlign: 'center' }}>
                                    <Turnstile
                                        ref={turnstileRef}
                                        siteKey={siteKey}
                                        onSuccess={async (token) => {
                                            setTurnstileToken(token);
                                            try {
                                                const jsonToken = await getUploadToken(token);
                                                setUploadToken(jsonToken);
                                                messageApi.success({
                                                    content: 'Security verification passed',
                                                    icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                                });
                                            } catch (error) {
                                                messageApi.error({
                                                    content: 'Failed to get upload authorization',
                                                    icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                                                });
                                                setUploadToken(null);
                                                setTurnstileToken(null);
                                            }
                                        }}
                                        onError={() => {
                                            setTurnstileToken(null);
                                            setUploadToken(null);
                                            messageApi.error({
                                                content: 'Security verification failed',
                                                icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                                            });
                                        }}
                                        onExpire={() => {
                                            setTurnstileToken(null)
                                            setUploadToken(null);
                                            messageApi.warning({
                                                content: 'Security verification expired',
                                                icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                                            });
                                        }}
                                        options={{
                                            theme: 'light',
                                            size: screens.xs ? 'compact' : 'normal'
                                        }}
                                    />
                                </div>
                            </Form.Item> */}

                            {/* Submit Button */}
                            <Form.Item style={{ marginTop: 24 }}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    size="large"
                                    block
                                    loading={uploading}
                                    // disabled={!turnstileToken}
                                    icon={uploading ? null : <CheckCircleOutlined />}
                                >
                                    {uploading ? t("Processing...") : t("Submit Registration")}
                                </Button>
                            </Form.Item>
                        </Form>
                    </Card>
                </div>
            </Content>
            <InfoModal
                open={modalConfig.open}
                onClose={() => setModalConfig({ ...modalConfig, open: false })}
                title={modalConfig.title}
                content={modalConfig.content}
                image={modalConfig.image}
            />
        </Layout>
    );
}